import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getClusterStyle } from "../lib/moodLanes";
import { resolveRecipe, rememberRecipe } from "../lib/stylePacks";

const STYLE_META = {
    relatable: { label: "Relatable", accent: "#9FE7D9" },
    poetic:    { label: "Poetic",    accent: "#F2B49B" },
    bold:      { label: "Bold",      accent: "#E5E5EA" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);
export { STYLE_META };

/**
 * iter-104 — Emphasis Mode renderer.
 *
 * Detects `*word*` markdown in body text and renders those words in the
 * pack's accent color, slightly larger, italic. Keeps everything inline
 * (spans only) so the shrink-to-fit measure loop still works.
 *
 * Pattern is intentionally narrow: `*non-whitespace-run*` (max ~24 chars).
 * Anything fancier would risk false positives in casual text.
 */
const EMPHASIS_RE = /\*([^*\n]{1,24}?)\*/g;

export function renderEmphasized(text, accentColor) {
    if (!text || typeof text !== "string") return text;
    if (!text.includes("*")) return text;
    const parts = [];
    let last = 0;
    let m;
    EMPHASIS_RE.lastIndex = 0;
    while ((m = EMPHASIS_RE.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push(
            <span
                key={`em-${m.index}`}
                style={{
                    color: accentColor,
                    fontStyle: "italic",
                    fontSize: "1.12em",
                    fontWeight: 500,
                    letterSpacing: "0.005em",
                }}
            >
                {m[1]}
            </span>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : text;
}



/**
 * Pack-aware visual treatment. Returns per-pack typography + a
 * READABILITY profile that drives shadow / scrim / glass-panel rules.
 *
 * Readability profiles:
 *   - "dark-bg"   → light text, soft top-to-bottom dark scrim, strong shadow
 *   - "light-bg"  → dark text, subtle bottom scrim, gentle shadow
 *   - "busy-bg"   → glass panel behind text + strong shadow
 *   - "minimal"   → almost no overlay (clean by design)
 */
export function packTreatment(pack, style) {
    switch (pack) {
        case "polaroid":
            return {
                textColor: "#2A1F18",
                accentColor: "#9C5A3C",
                bodyFont: style === "bold" ? "font-sans font-semibold uppercase tracking-tight" : "font-serif font-light italic",
                titleFont: "font-serif italic",
                vignetteOpacity: 0.18,
                labelColor: "rgba(42, 31, 24, 0.72)",
                watermarkColor: "rgba(42, 31, 24, 0.55)",
                grainBlend: "multiply",
                cssBg: "linear-gradient(180deg, #F8EDD9 0%, #F4DFB9 55%, #E5C291 100%)",
                readability: "light-bg",
            };
        case "watercolor":
            return {
                textColor: "#15233C",
                accentColor: "#3F6B8E",
                bodyFont: style === "bold" ? "font-sans font-semibold tracking-tight" : "font-serif font-light",
                titleFont: "font-serif italic font-light",
                vignetteOpacity: 0.10,
                labelColor: "rgba(21, 35, 60, 0.65)",
                watermarkColor: "rgba(21, 35, 60, 0.40)",
                grainBlend: "multiply",
                cssBg: "radial-gradient(80% 60% at 20% 20%, #D9E7F2 0%, transparent 60%), radial-gradient(70% 60% at 80% 80%, #F2D9E2 0%, transparent 60%), radial-gradient(60% 50% at 50% 100%, #E2EBD4 0%, transparent 60%), #F5F2EC",
                readability: "busy-bg",
            };
        case "vintage":
            return {
                textColor: "#3A1F18",
                accentColor: "#7A3E20",
                bodyFont: style === "bold" ? "font-sans font-semibold uppercase tracking-tight" : "font-serif font-light italic",
                titleFont: "font-serif italic",
                vignetteOpacity: 0.35,
                labelColor: "rgba(58, 31, 24, 0.85)",
                watermarkColor: "rgba(58, 31, 24, 0.55)",
                grainBlend: "multiply",
                readability: "busy-bg",
            };
        case "brutalist":
            return {
                textColor: style === "poetic" ? "#F5F0E6" : "#0E0E10",
                accentColor: style === "bold" ? "#FF5A1F" : (style === "poetic" ? "#F5F0E6" : "#0E0E10"),
                bodyFont: "font-sans font-bold uppercase tracking-tight",
                titleFont: "font-sans font-bold uppercase tracking-[0.05em]",
                vignetteOpacity: 0.0,
                labelColor: style === "poetic" ? "rgba(245,240,230,0.7)" : "rgba(14,14,16,0.7)",
                watermarkColor: style === "poetic" ? "rgba(245,240,230,0.6)" : "rgba(14,14,16,0.6)",
                grainBlend: "multiply",
                readability: "minimal",
            };
        case "minimal":
            return {
                textColor: style === "bold" ? "#F5F0E6" : "#1A1715",
                accentColor: style === "bold" ? "#F5F0E6" : "#8A857E",
                bodyFont: style === "bold" ? "font-sans font-light tracking-tight" : "font-serif font-light",
                titleFont: "font-serif italic font-light",
                vignetteOpacity: 0.10,
                labelColor: style === "bold" ? "rgba(245,240,230,0.6)" : "rgba(26,23,21,0.55)",
                watermarkColor: style === "bold" ? "rgba(245,240,230,0.45)" : "rgba(26,23,21,0.40)",
                grainBlend: style === "bold" ? "overlay" : "multiply",
                readability: style === "bold" ? "dark-bg" : "minimal",
            };
        case "cosmic":
            // Holographic aurora-shift. Iridescent purple→pink→cyan→teal gradient.
            // Star particles + iridescent edge sheen handled by .mc-pack-cosmic CSS layer.
            return {
                textColor: "#FFFFFF",
                accentColor: "#B9A6FF",
                bodyFont: style === "bold" ? "font-sans font-semibold tracking-tight" : "font-serif font-light italic",
                titleFont: "font-serif italic",
                vignetteOpacity: 0.30,
                labelColor: "rgba(255,255,255,0.78)",
                watermarkColor: "rgba(255,255,255,0.55)",
                grainBlend: "overlay",
                cssBg: "radial-gradient(120% 100% at 20% 10%, #6E4BFF 0%, transparent 55%), radial-gradient(110% 90% at 85% 30%, #FF6FB1 0%, transparent 60%), radial-gradient(90% 80% at 50% 90%, #2BC5C0 0%, transparent 55%), radial-gradient(80% 70% at 10% 80%, #B07CFF 0%, transparent 60%), linear-gradient(180deg, #0B0721 0%, #16092C 60%, #0A0518 100%)",
                materialLayer: "cosmic",
                readability: "dark-bg",
            };
        case "romantic":
            // Translucent vellum / glass with warm rose-gold accents.
            // Soft inner glow handled by .mc-pack-romantic.
            return {
                textColor: "#3A1F1A",
                accentColor: "#B8736A",
                bodyFont: style === "bold" ? "font-sans font-semibold tracking-tight" : "font-serif font-light italic",
                titleFont: "font-serif italic",
                vignetteOpacity: 0.12,
                labelColor: "rgba(58,31,26,0.72)",
                watermarkColor: "rgba(58,31,26,0.42)",
                grainBlend: "multiply",
                cssBg: "radial-gradient(80% 80% at 30% 25%, #FBE3DA 0%, transparent 60%), radial-gradient(80% 80% at 75% 80%, #F4C9C2 0%, transparent 60%), linear-gradient(180deg, #FFF1EB 0%, #F8DDD3 100%)",
                materialLayer: "romantic",
                readability: "busy-bg",
            };
        case "dark":
            // Obsidian gloss black with sharp metallic edge highlights + deep specular.
            return {
                textColor: "#F5F0E6",
                accentColor: style === "bold" ? "#E8C770" : "#C7C2BA",
                bodyFont: style === "bold" ? "font-sans font-bold uppercase tracking-tight" : "font-serif font-light italic",
                titleFont: style === "bold" ? "font-sans font-bold uppercase tracking-[0.05em]" : "font-serif italic",
                vignetteOpacity: 0.55,
                labelColor: "rgba(245,240,230,0.72)",
                watermarkColor: "rgba(245,240,230,0.32)",
                grainBlend: "overlay",
                cssBg: "radial-gradient(120% 100% at 50% 0%, #1A1A1F 0%, #0A0A0C 55%, #050506 100%)",
                materialLayer: "dark",
                readability: "dark-bg",
            };
        case "receipt":
            // iter-84 — Thermal-paper receipt aesthetic. Mono everything,
            // soft cream background, restrained crimson accent. The layout
            // template ("receipt") is applied on top so body lines render
            // as ITEM/QTY/TOTAL stacked rows.
            return {
                textColor: "#181410",
                accentColor: "#9C2A1A",
                bodyFont: "font-mono uppercase tracking-wider font-medium",
                titleFont: "font-mono uppercase tracking-widest",
                vignetteOpacity: 0.08,
                labelColor: "rgba(24,20,16,0.55)",
                watermarkColor: "rgba(24,20,16,0.32)",
                grainBlend: "multiply",
                cssBg: "linear-gradient(180deg, #FBF6E8 0%, #F2EAD3 100%)",
                templateKind: "receipt",
                readability: "light-bg",
            };
        case "newspaper":
            // iter-84 — Newsprint cream + black ink + crimson accent. The
            // template renders the body as a BREAKING-NEWS headline block.
            return {
                textColor: "#0A0908",
                accentColor: "#B0321A",
                bodyFont: "font-serif font-medium",
                titleFont: "font-condensed uppercase tracking-tight",
                vignetteOpacity: 0.10,
                labelColor: "rgba(10,9,8,0.62)",
                watermarkColor: "rgba(10,9,8,0.30)",
                grainBlend: "multiply",
                cssBg: "linear-gradient(180deg, #F6EFE2 0%, #E8DEC8 100%)",
                templateKind: "newspaper",
                readability: "light-bg",
            };
        case "ticket":
            // iter-84 — Concert / travel stub. Cream face + perforated edge
            // simulated in the template renderer. Two-column header (admit
            // one + serial number) flanks the central feeling.
            return {
                textColor: "#1F1612",
                accentColor: "#9A2D1A",
                bodyFont: "font-display uppercase tracking-tight font-medium",
                titleFont: "font-condensed uppercase tracking-tight",
                vignetteOpacity: 0.18,
                labelColor: "rgba(31,22,18,0.65)",
                watermarkColor: "rgba(31,22,18,0.32)",
                grainBlend: "multiply",
                cssBg: "linear-gradient(180deg, #F7E9C8 0%, #E8C898 100%)",
                templateKind: "ticket",
                readability: "light-bg",
            };
        case "cinematic":
        default:
            return {
                textColor: "#FAFAFA",
                accentColor: STYLE_META[style]?.accent || "#FAFAFA",
                bodyFont: style === "bold" ? "font-sans font-semibold uppercase tracking-tight" : "font-serif font-light",
                titleFont: "font-serif italic",
                vignetteOpacity: 0.55,
                labelColor: "rgba(255,255,255,0.7)",
                watermarkColor: "rgba(255,255,255,0.55)",
                grainBlend: "overlay",
                readability: "dark-bg",
            };
    }
}

/**
 * Universal readability profile — returns shadow/scrim/glass rules.
 * Every card text layer uses these so text never disappears against
 * bright, busy, or animated backgrounds.
 */
// ── Contrast helpers — WCAG-relative-luminance based. ──
function _parseHex(c) {
    if (!c) return null;
    const s = c.trim();
    if (s.startsWith("#")) {
        const h = s.slice(1);
        if (h.length === 3) {
            return [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)];
        }
        if (h.length === 6) {
            return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
        }
    }
    return null;
}
function _luminance(rgb) {
    if (!rgb) return 0;
    const [r, g, b] = rgb.map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function _contrast(c1, c2) {
    const l1 = _luminance(_parseHex(c1));
    const l2 = _luminance(_parseHex(c2));
    const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (a + 0.05) / (b + 0.05);
}
// Extract every #RRGGBB color stop from a CSS background expression.
function _extractColors(css) {
    if (!css) return [];
    const m = css.match(/#[0-9a-fA-F]{6}/g);
    return m || [];
}
/**
 * Sample the recipe's background color stops vs. the text color.
 * Run a graduated 7-step repair if contrast is below WCAG AA (4.5:1):
 *
 *   step 1 — try the paired alt text color (lightAlt / darkAlt)
 *   step 2 — apply a subtle gradient veil overlay
 *   step 3 — bump readability to "busy-bg" (glass panel behind text)
 *   step 4 — force a strong text shadow + stroke
 *   step 5+ — accept worst-case (no infinite recursion)
 *
 * Returns the augmented treatment with `_repairTier` set so debug
 * panels / analytics can see which level kicked in.
 */
function autoFortifyContrast(treatment) {
    if (!treatment || !treatment._recipe) return treatment;
    const text = treatment.textColor;
    const stops = _extractColors(treatment.cssBg || "");
    if (!stops.length) return treatment;

    const _contrastVs = (textColor) => {
        let worst = 21;
        for (const s of stops) {
            const c = _contrast(textColor, s);
            if (c < worst) worst = c;
        }
        return worst;
    };

    let worst = _contrastVs(text);
    if (worst >= 4.5) return { ...treatment, _repairTier: 0 };

    let working = { ...treatment, _repairTier: 1 };

    // Step 1 — swap to paired alt text color if recipe declared one and it
    // improves contrast. Recipes can opt in via `lightAltTextColor` or
    // `darkAltTextColor` on the variant.
    const altKey = treatment._recipe.lightAltTextColor || treatment._recipe.darkAltTextColor;
    if (altKey) {
        const altContrast = _contrastVs(altKey);
        if (altContrast > worst && altContrast >= 4.5) {
            return { ...working, textColor: altKey, _repairTier: 1, _repairReason: "alt-color-swap" };
        }
    }

    // Step 2 — gradient veil. Pre-multiply a soft black or white veil over
    // the bg in the direction of better readability for the text color.
    // Recipes can opt out by setting `noVeil: true` on the variant.
    if (!treatment._recipe.noVeil) {
        const textIsLight = _luminance(_parseHex(text)) > 0.5;
        const veil = textIsLight
            ? "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.32) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.30) 100%)";
        working = {
            ...working,
            cssBg: `${veil}, ${treatment.cssBg}`,
            _repairTier: 2,
            _repairReason: "veil",
        };
        const newStops = _extractColors(working.cssBg);
        let newWorst = 21;
        for (const s of newStops) {
            const c = _contrast(text, s);
            if (c < newWorst) newWorst = c;
        }
        if (newWorst >= 4.5) return working;
        worst = newWorst;
    }

    // Step 3 — glass panel via readability bump.
    working = { ...working, readability: "busy-bg", _repairTier: 3, _repairReason: "glass-panel" };

    // Step 4 — if STILL likely insufficient, mark it for forced text shadow
    // bump. readabilityRules picks this up via _forceStrongShadow.
    if (worst < 3.2) {
        working._forceStrongShadow = true;
        working._repairTier = 4;
        working._repairReason = "forced-shadow";
    }

    return working;
}

export function readabilityRules(profile, textColor) {
    const isLightText = textColor.toUpperCase().startsWith("#F") || textColor.toUpperCase().startsWith("#E");
    switch (profile) {
        case "dark-bg":
            return {
                bodyShadow: "0 0 18px rgba(0,0,0,0.45), 0 4px 18px rgba(0,0,0,0.65)",
                titleShadow: "0 2px 12px rgba(0,0,0,0.55)",
                labelShadow: "0 1px 6px rgba(0,0,0,0.55)",
                scrim: "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.0) 22%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.42) 100%)",
                glass: null,
            };
        case "light-bg":
            return {
                bodyShadow: "0 1px 4px rgba(255,255,255,0.55), 0 2px 10px rgba(255,255,255,0.35)",
                titleShadow: "0 1px 3px rgba(255,255,255,0.55)",
                labelShadow: "none",
                scrim: "linear-gradient(180deg, rgba(255,255,255,0.0) 50%, rgba(0,0,0,0.08) 100%)",
                glass: null,
            };
        case "busy-bg":
            return {
                bodyShadow: isLightText
                    ? "0 2px 14px rgba(0,0,0,0.55), 0 0 22px rgba(0,0,0,0.35)"
                    : "0 1px 6px rgba(255,255,255,0.6), 0 2px 14px rgba(255,255,255,0.4)",
                titleShadow: isLightText
                    ? "0 2px 10px rgba(0,0,0,0.6)"
                    : "0 1px 4px rgba(255,255,255,0.55)",
                labelShadow: isLightText
                    ? "0 1px 4px rgba(0,0,0,0.5)"
                    : "0 1px 3px rgba(255,255,255,0.5)",
                scrim: isLightText
                    ? "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.35))"
                    : "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.18))",
                glass: {
                    background: isLightText ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.22)",
                    backdropFilter: "blur(10px)",
                    border: isLightText ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.06)",
                    borderRadius: "22px",
                },
            };
        case "minimal":
        default:
            return {
                bodyShadow: isLightText
                    ? "0 1px 6px rgba(0,0,0,0.30)"
                    : "0 1px 2px rgba(255,255,255,0.35)",
                titleShadow: "none",
                labelShadow: "none",
                scrim: null,
                glass: null,
            };
    }
}

/**
 * Convert a Style Pack v2 recipe → the same shape as packTreatment() so
 * the render pipeline below stays untouched. When a recipe is supplied,
 * its values fully replace the legacy treatment.
 */
function recipeToTreatment(recipe, style) {
    if (!recipe || !recipe.variant) return null;
    const v = recipe.variant;
    return {
        textColor: v.textColor,
        accentColor: v.accentColor,
        bodyFont: `${v.bodyFontClass || "font-serif"} ${v.bodyWeightClass || ""} ${v.bodyTransformClass || ""}`.trim(),
        titleFont: v.titleFontClass || "font-serif italic",
        vignetteOpacity: v.treatment?.vignette ?? 0.30,
        labelColor: v.labelColor,
        watermarkColor: v.watermarkColor,
        grainBlend: v.readability === "light-bg" ? "multiply" : "overlay",
        cssBg: v.background,
        readability: v.readability || "dark-bg",
        materialLayer: null,
        // Forward recipe so downstream layers can read pattern/style hints.
        _recipe: v,
    };
}

export const MoodCard = forwardRef(({ card, size = "preview", showWatermark = true, lane = null, temperature = null, qrUrl = null, variant = null, recipe = null, enableMotion = false, moodId = null }, ref) => {
    // ALL hooks must run in the same order on every render — declare them
    // BEFORE any early return. (Previously `if (!card) return null` lived
    // above the useRef/useLayoutEffect calls, which violated the rules of
    // hooks and broke the eslint build.)
    const isFull = size === "full";
    const bodyBoxRef = useRef(null);
    const bodyTextRef = useRef(null);
    const titleTextRef = useRef(null);
    // Auto-shrink-to-fit for long-text cards in full (export) mode. Walks the
    // font-size down in 4px steps until the body fits inside its container,
    // capped at 14 iterations and a 56px floor. No-op for preview mode or
    // when the card prop is missing.
    useLayoutEffect(() => {
        if (!card) return;
        // ── Body shrink — runs for BOTH preview and full export.
        // Compares the WRAPPER (title + watermark + body) against the
        // BOX's allocated flex space. The box has `flex-1 min-h-0` so
        // its clientHeight reflects the actual allocated height after
        // header + footer claim their share. The wrapper has no height
        // constraint, so wrapper.scrollHeight is the natural content
        // height. If wrapper > box, walk the body font-size down until
        // it fits. Preview floor: 11px; full export floor: 56px.
        const box = bodyBoxRef.current;
        const text = bodyTextRef.current;
        const wrapper = text?.parentElement;
        if (box && text && wrapper) {
            text.style.fontSize = "";
            const computedSize = parseFloat(window.getComputedStyle(text).fontSize) || (isFull ? 104 : 30);
            let fs = computedSize;
            const floor = isFull ? 56 : 11;
            const step = isFull ? 4 : 1.5;
            for (let i = 0; i < 28; i += 1) {
                const heightOverflow = wrapper.scrollHeight > box.clientHeight + 1;
                const widthOverflow = text.scrollWidth > box.clientWidth + 1;
                if (!heightOverflow && !widthOverflow) break;
                if (fs <= floor) break;
                fs -= step;
                text.style.fontSize = `${fs}px`;
            }
        }
        // ── Title shrink — keep hashtag on ONE line. Measures by line-count
        //    (scrollHeight / line-height) and walks font-size down until the
        //    title renders on a single line or hits the floor. Robust against
        //    background-image-induced reflow because we re-measure every
        //    render via useLayoutEffect. ──
        const title = titleTextRef.current;
        const titleParent = title?.parentElement;
        if (title && titleParent) {
            title.style.fontSize = "";
            const initial = parseFloat(window.getComputedStyle(title).fontSize) || 28;
            let tfs = initial;
            const tfloor = isFull ? 28 : 11;
            for (let i = 0; i < 22; i += 1) {
                const lineHeightPx = parseFloat(window.getComputedStyle(title).lineHeight) || tfs * 1.1;
                const lines = Math.round(title.scrollHeight / lineHeightPx);
                if (lines <= 1) break;
                if (tfs <= tfloor) break;
                tfs -= Math.max(1, Math.round(initial * 0.04));
                title.style.fontSize = `${tfs}px`;
            }
        }
    });

    if (!card) return null;
    const pack = card.pack || "cinematic";
    // Resolve a Style Pack v2 recipe if either a recipe prop is passed OR
    // we can derive one from the card's pack + recipe_id. Falls back to
    // legacy packTreatment if no recipe is found (keeps old cards working).
    //
    // ⚠️ moodId resolution order: explicit `moodId` prop FIRST (preferred —
    // parent passes the URL :id), then card.id as a safety fallback. The
    // individual style-card objects don't carry a per-mood id, so without
    // `moodId` we used to fall back to undefined → _hash(undefined)=0 →
    // variants[0] always. That made the in-app preview (which used the
    // wrong path) diverge from the downloaded PNG (which uses the right
    // mood id via the export URL). Fixing both surfaces to read the
    // same moodId removes the divergence.
    const activeRecipe = recipe || resolveRecipe({
        moodId: moodId || card.id,
        pack,
        recipeId: card.recipe_id || null,
    });
    const recipeT = recipeToTreatment(activeRecipe, card.style);
    const tBase = recipeT || packTreatment(pack, card.style);
    // ── Auto-contrast guardrail ──
    // Sample the recipe's background color stops vs. the text color. If
    // worst-case contrast drops below WCAG AA (4.5:1), elevate the
    // readability profile to "busy-bg" which adds a translucent glass
    // panel behind the text. This is a defensive net; the recipe authors
    // already pre-tested the dominant cases.
    const t = autoFortifyContrast(tBase);
    // ── Creator brand-color tinting ──
    // Premium creators can store up to 3 brand colors. We tastefully apply
    // them WITHOUT breaking recipe legibility:
    //   [0] → accentColor (the recipe's emphasis hue — used for QR ring,
    //         premium accent pulse, secondary text)
    //   [1] → labelColor blend (subtle — at 30% opacity over recipe value)
    //   [2] → watermarkColor blend (very subtle — at 25% opacity)
    // Recipes keep their textColor / background untouched, so contrast
    // guardrails still hold.
    const brand = Array.isArray(card.creator_brand_colors) ? card.creator_brand_colors : null;
    if (brand && brand.length > 0) {
        if (brand[0]) t.accentColor = brand[0];
        if (brand[1]) {
            // Blend brand[1] over the existing labelColor at 30% — preserves
            // light/dark theme balance while picking up a creator hint.
            t.labelColor = `color-mix(in srgb, ${brand[1]} 30%, ${t.labelColor} 70%)`;
        }
        if (brand[2]) {
            t.watermarkColor = `color-mix(in srgb, ${brand[2]} 25%, ${t.watermarkColor} 75%)`;
        }
    }
    const r = readabilityRules(t.readability, t.textColor);
    // Tier-4 contrast repair: if autoFortify decided even glass panel isn't
    // enough, force a strong text shadow on top of whatever readability set.
    if (t._forceStrongShadow) {
        r.titleShadow = "0 2px 16px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.9)";
        r.labelShadow = "0 1px 8px rgba(0,0,0,0.85)";
    }

    // Phase 3·b — cluster-aware visual adaptation. Falls through to no-op if
    // lane or temperature isn't known. Lane + temperature come from props
    // (passed by CardDetail/PublicCard from the moodcard root) — they're NOT
    // per-card fields.
    const cluster = getClusterStyle(lane, temperature);

    // Full-size (export) typography MUST visually match the in-app preview.
    // In-app uses text-3xl/4xl (~30-36px) on a ~390-420px wide mobile card —
    // that's ~7-9% of card width per character. The export canvas is 1080×1920;
    // to preserve the same visual ratio the body must be ~80-120px, not 56.
    // Style-aware because "bold" cards are designed to feel louder.
    const titleClass = isFull ? "text-[68px] leading-[1.05]" : "text-xl sm:text-2xl";
    const bodyClass = isFull
        ? (card.style === "bold"
            ? "text-[124px] leading-[1.05]"
            : card.style === "poetic"
            ? "text-[96px] leading-[1.18]"
            : "text-[104px] leading-[1.15]")  // relatable
        : (card.style === "bold" ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl");

    return (
        <div
            ref={ref}
            data-testid={`mood-card-${card.style}`}
            data-cluster={cluster ? `${lane}:${temperature}` : undefined}
            className={`relative w-full aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 hover-tilt${
                enableMotion && activeRecipe?.variant?.motion?.type
                    ? ` motion-${activeRecipe.variant.motion.type}`
                    : ""
            }`}
            style={{
                backgroundColor: "#050507",
                // Phase 3·b — tilt (only on full-size to avoid layout shift in grids).
                transform: cluster?.tilt && isFull ? `rotate(${cluster.tilt}deg)` : undefined,
                // Phase 3·b — shadow-boost layers an extra deep shadow on top of base.
                boxShadow: cluster?.shadowBoost
                    ? `0 30px 80px -20px rgba(0,0,0,0.8), 0 40px 100px -10px rgba(0,0,0,${0.4 + cluster.shadowBoost})`
                    : "0 30px 80px -20px rgba(0,0,0,0.8)",
            }}
        >
            {/* Background — image OR CSS gradient for CSS-only packs.
                When a variant is supplied (Premium swap drawer OR the
                deterministic per-mood default), its CSS filter is applied
                to ONLY this layer — text/accent/watermark above are
                untouched so legibility never degrades. */}
            {t.cssBg ? (
                <div
                    className="absolute inset-0"
                    style={{
                        background: t.cssBg,
                        filter: variant?.filter && variant.filter !== "none" ? variant.filter : undefined,
                    }}
                />
            ) : (
                <img
                    src={absUrl(card.background_url)}
                    alt=""
                    crossOrigin="anonymous"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                    style={{
                        filter: variant?.filter && variant.filter !== "none" ? variant.filter : undefined,
                    }}
                />
            )}
            {/* Soft vignette */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        `radial-gradient(120% 80% at 50% 50%, transparent 30%, rgba(0,0,0,${t.vignetteOpacity}) 100%)`,
                }}
            />
            {/* Universal readability scrim — guarantees text contrast across any background */}
            {r.scrim && (
                <div className="absolute inset-0 pointer-events-none" style={{ background: r.scrim }} />
            )}
            {/* Grain */}
            <div
                className="absolute inset-0 grain"
                style={{ mixBlendMode: t.grainBlend }}
            />
            {/* Material overlay — only for premium material packs (cosmic / romantic / dark) */}
            {t.materialLayer === "cosmic" && (
                <>
                    <div className="absolute inset-0 pointer-events-none mc-pack-cosmic-shimmer" />
                    <div className="absolute inset-0 pointer-events-none mc-pack-cosmic-stars" />
                </>
            )}
            {t.materialLayer === "romantic" && (
                <div className="absolute inset-0 pointer-events-none mc-pack-romantic-glow" />
            )}
            {t.materialLayer === "dark" && (
                <div className="absolute inset-0 pointer-events-none mc-pack-dark-specular" />
            )}

            {/* Phase 3·b — cluster tint overlay. Inset cast in the lane's accent
                color at 5-12% opacity. Layered above pack treatment, below text. */}
            {cluster?.tintRGBA && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: `radial-gradient(140% 100% at 50% 100%, ${cluster.tintRGBA} 0%, transparent 70%)`,
                        mixBlendMode: "screen",
                    }}
                />
            )}

            {/* Diagonal soft watermark — only when showWatermark */}
            {showWatermark && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none flex items-center justify-center"
                    style={{ transform: "rotate(-30deg)" }}
                >
                    <div
                        className="font-serif italic whitespace-nowrap"
                        style={{
                            color: t.watermarkColor,
                            fontSize: isFull ? "84px" : "28px",
                            opacity: 0.10,
                            letterSpacing: "0.05em",
                            mixBlendMode: pack === "minimal" && card.style !== "bold" ? "multiply" : "screen",
                        }}
                    >
                        Made with MoodCard · Made with MoodCard
                    </div>
                </div>
            )}

            {/* iter-84 — Viral-template scaffolding (receipt / newspaper / ticket).
                Renders only when the active pack sets templateKind. Sits
                ABOVE the background/grain but BELOW the content layer so
                the text remains the primary read. */}
            {t.templateKind === "receipt" && (
                <div className="absolute inset-x-0 top-0 pt-3 px-5 flex flex-col items-center pointer-events-none z-[1]" aria-hidden="true">
                    <div
                        className="font-mono uppercase tracking-[0.32em] text-[10px]"
                        style={{ color: t.accentColor, opacity: 0.95 }}
                    >
                        ✦ moodcard receipt
                    </div>
                    <div
                        className="font-mono text-[9px] mt-1"
                        style={{ color: t.labelColor, letterSpacing: "0.18em" }}
                    >
                        store · #001 · order · {(card.id || "x").slice(-6).toUpperCase()}
                    </div>
                    <div
                        className="w-full mt-2"
                        style={{
                            borderTop: `1px dashed ${t.labelColor}`,
                            opacity: 0.55,
                        }}
                    />
                </div>
            )}
            {t.templateKind === "receipt" && (
                <div className="absolute inset-x-0 bottom-10 px-5 flex flex-col items-center pointer-events-none z-[1]" aria-hidden="true">
                    <div
                        className="w-full mb-2"
                        style={{
                            borderTop: `1px dashed ${t.labelColor}`,
                            opacity: 0.55,
                        }}
                    />
                    <div
                        className="font-mono uppercase tracking-widest text-[10px]"
                        style={{ color: t.accentColor }}
                    >
                        ── total: priceless ──
                    </div>
                </div>
            )}
            {t.templateKind === "newspaper" && (
                <div className="absolute inset-x-0 top-0 pt-3 px-5 flex flex-col items-center pointer-events-none z-[1]" aria-hidden="true">
                    <div
                        className="font-condensed uppercase tracking-[0.42em] text-[10px] py-1 px-3 rounded-sm"
                        style={{
                            color: t.cssBg ? "#F6EFE2" : t.textColor,
                            backgroundColor: t.accentColor,
                            letterSpacing: "0.32em",
                        }}
                    >
                        ◆ breaking news
                    </div>
                    <div
                        className="font-serif italic text-[9px] mt-2"
                        style={{ color: t.labelColor }}
                    >
                        the moodcard times · vol. 1 · daily edition
                    </div>
                    <div
                        className="w-full mt-2"
                        style={{
                            borderTop: `2px solid ${t.textColor}`,
                            opacity: 0.62,
                        }}
                    />
                </div>
            )}
            {t.templateKind === "ticket" && (
                <>
                    {/* Perforated edges (left/right) */}
                    <div
                        className="absolute left-3 top-3 bottom-3 pointer-events-none z-[1]"
                        aria-hidden="true"
                        style={{
                            width: 0,
                            borderLeft: `2px dashed ${t.labelColor}`,
                            opacity: 0.55,
                        }}
                    />
                    <div
                        className="absolute right-3 top-3 bottom-3 pointer-events-none z-[1]"
                        aria-hidden="true"
                        style={{
                            width: 0,
                            borderRight: `2px dashed ${t.labelColor}`,
                            opacity: 0.55,
                        }}
                    />
                    {/* Top "ADMIT ONE" stamp */}
                    <div className="absolute inset-x-0 top-0 pt-3 px-7 flex items-center justify-between pointer-events-none z-[1]" aria-hidden="true">
                        <div
                            className="font-condensed uppercase tracking-[0.32em] text-[10px] py-0.5 px-2 rounded-sm border"
                            style={{
                                color: t.accentColor,
                                borderColor: t.accentColor,
                                opacity: 0.92,
                            }}
                        >
                            admit one
                        </div>
                        <div
                            className="font-mono text-[9px] tracking-widest"
                            style={{ color: t.labelColor }}
                        >
                            #{(card.id || "x").slice(-6).toUpperCase()}
                        </div>
                    </div>
                </>
            )}

            {/* Content — safe insets ensure text never touches the edge */}
            <div
                className="relative h-full w-full flex flex-col justify-between p-7 sm:p-9"
                style={{ color: t.textColor }}
            >
                {/* Top wordmark removed per user feedback — the underlined
                    moodcard.app in the footer is enough brand presence; the
                    top "MoodCard" word was redundant and stole visual focus
                    from the hashtag/title. */}

                {/* Center text — wrapped in optional glass panel for busy backgrounds.
                    `min-h-0` is the critical flex-layout fix: without it, this
                    container's content can push its own height beyond the
                    parent card's bounds (flexbox min-height: auto default).
                    With min-h-0, content stays inside the allocated flex
                    space and the shrink-to-fit loop measures correctly. */}
                <div className="flex-1 min-h-0 flex flex-col justify-center items-center text-center -mt-4" ref={bodyBoxRef}>
                    <div
                        className="w-full max-w-[92%] px-3 py-2"
                        style={r.glass ? { ...r.glass, paddingTop: isFull ? "44px" : "20px", paddingBottom: isFull ? "44px" : "20px", paddingLeft: isFull ? "44px" : "20px", paddingRight: isFull ? "44px" : "20px" } : null}
                    >
                        {card.title && (
                            <div
                                ref={titleTextRef}
                                className={`${t.titleFont} mb-3 ${titleClass}`}
                                style={{
                                    color: t.accentColor,
                                    fontWeight: 400,
                                    opacity: 0.92,
                                    textShadow: r.titleShadow,
                                    letterSpacing: "0.04em",
                                    // Hashtag preferences:
                                    //   1. Stay on a single line — the shrink loop above walks
                                    //      the font-size down (initial → floor) so it fits.
                                    //   2. If even at floor it'd overflow, allow wrap on the
                                    //      underscore separators (we replace " "→"_" in the
                                    //      hashtag, so this preserves word boundaries).
                                    maxWidth: "100%",
                                    overflowWrap: "anywhere",
                                    lineHeight: 1.08,
                                }}
                            >
                                #{(card.title || "").replace(/\s+/g, "_").toUpperCase()}
                            </div>
                        )}
                        {/* iter-75: subtle "moodcard" stamp sits BETWEEN the title
                            and the body — brand reinforcement that doesn't
                            distract from the words. Uses the title's accent
                            color at low opacity so it inherits the pack's vibe. */}
                        {card.title && (
                            <div
                                aria-hidden
                                className="font-sans tracking-[0.42em] uppercase mb-4"
                                style={{
                                    color: t.accentColor,
                                    opacity: 0.32,
                                    fontSize: "0.62rem",
                                    fontWeight: 600,
                                }}
                            >
                                · moodcard ·
                            </div>
                        )}
                        <div
                            ref={bodyTextRef}
                            className={`${t.bodyFont} ${bodyClass} whitespace-pre-line`}
                            style={{
                                color: t.textColor,
                                textShadow: r.bodyShadow,
                                lineHeight: cluster?.lineHeight ?? (card.style === "bold" ? 1.1 : undefined),
                                letterSpacing: cluster?.letterSpacing || undefined,
                            }}
                        >
                            {renderEmphasized(card.body, t.accentColor)}
                        </div>
                    </div>
                </div>

                {/* Creator signature line — Premium creator's personal sign-off
                    (e.g. "— George N., Brooklyn"). Sits ABOVE the brand footer.
                    Hidden when card has no creator_signature_line.
                    Uses creator_brand_colors[1] at a HIGHER 60% blend (vs 30%
                    elsewhere) so the signature reads as the creator's voice,
                    not just another label. */}
                {card.creator_signature_line && (() => {
                    const sigBrand = Array.isArray(card.creator_brand_colors) ? card.creator_brand_colors[1] : null;
                    const sigColor = sigBrand
                        ? `color-mix(in srgb, ${sigBrand} 60%, ${t.labelColor} 40%)`
                        : t.labelColor;
                    return (
                        <div
                            data-testid="moodcard-creator-signature"
                            className="text-center mb-2"
                        >
                            <span
                                className="font-serif italic"
                                style={{
                                    color: sigColor,
                                    textShadow: r.labelShadow,
                                    fontSize: isFull ? "28px" : "13px",
                                    opacity: 0.92,
                                    lineHeight: 1.1,
                                    letterSpacing: "0.01em",
                                }}
                            >
                                {card.creator_signature_line}
                            </span>
                        </div>
                    );
                })()}

                {/* Footer branding — "MADE WITH" + underlined "MOODCARD.APP".
                    Underline reads as a hyperlink visual cue without being
                    an actual link (cards are rasterized, so click impossible).
                    Combined into one whitespace-nowrap span; preview font 11px
                    so 220px-wide cards fit cleanly. */}
                <div className="flex items-baseline justify-center">
                    <span
                        className="font-sans uppercase whitespace-nowrap"
                        style={{
                            color: t.labelColor,
                            textShadow: r.labelShadow,
                            fontSize: isFull ? "26px" : "11px",
                            letterSpacing: "0.28em",
                            fontWeight: 500,
                            opacity: 0.82,
                            lineHeight: 1,
                        }}
                    >
                        MADE WITH{" "}
                        <span
                            style={{
                                textDecoration: "underline",
                                textDecorationThickness: isFull ? "2px" : "1px",
                                textUnderlineOffset: isFull ? "5px" : "2px",
                            }}
                        >
                            MOODCARD.APP
                        </span>
                    </span>
                </div>
            </div>

            {/* Share-back QR stamp — bottom-right corner, premium glassy treatment.
                Renders only when qrUrl prop is set. Free feature; anyone who
                sees the card can scan it and land on the remix page, which
                compounds organic discovery on every download/share.

                Sizing: 6.7% of card height for previews, ~8% on full export →
                always legible at IG-feed thumbnail size, never dominates. */}
            {/* Creator logo — sits in the TOP-LEFT corner so it doesn't fight
                the bottom-right QR code or the bottom-center brand stamp.
                Premium creator feature. Auto-applied via card.creator_logo_url
                at moodcard creation. Translucent so it reads as a watermark
                rather than a competing graphic. */}
            {card.creator_logo_url && (
                <div
                    data-testid="moodcard-creator-logo"
                    style={{
                        position: "absolute",
                        left: isFull ? 36 : 12,
                        top: isFull ? 36 : 12,
                        width: isFull ? 96 : 36,
                        height: isFull ? 96 : 36,
                        opacity: 0.85,
                        zIndex: 4,
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        alt=""
                        src={card.creator_logo_url.startsWith("/")
                            ? `${process.env.REACT_APP_BACKEND_URL || ""}${card.creator_logo_url}`
                            : card.creator_logo_url}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            filter: r.bodyShadow ? `drop-shadow(${r.labelShadow || "0 1px 4px rgba(0,0,0,0.45)"})` : undefined,
                        }}
                    />
                </div>
            )}

            {qrUrl && (
                <div
                    data-testid="moodcard-qr-stamp"
                    style={{
                        position: "absolute",
                        right: isFull ? 36 : 12,
                        bottom: isFull ? 36 : 12,
                        zIndex: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: isFull ? 8 : 3,
                    }}
                >
                    <div
                        style={{
                            // Soft glassy frame so the QR reads on light + dark packs
                            background: t.qrFrameBg || "rgba(255,255,255,0.94)",
                            padding: isFull ? 12 : 5,
                            borderRadius: isFull ? 10 : 5,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)",
                            lineHeight: 0,
                        }}
                    >
                        <QRCodeSVG
                            value={qrUrl}
                            size={isFull ? 128 : 56}
                            level="M"            // medium error-correction; balances scan reliability vs density
                            bgColor="transparent"
                            fgColor="#0a0a0f"    // near-black, scans well on white frame
                            includeMargin={false}
                        />
                    </div>
                    <span
                        className="font-sans uppercase"
                        style={{
                            color: t.labelColor,
                            textShadow: r.labelShadow,
                            fontSize: isFull ? "16px" : "8px",
                            letterSpacing: "0.22em",
                            opacity: 0.75,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                        }}
                    >
                        scan to remix
                    </span>
                </div>
            )}
        </div>
    );
});

MoodCard.displayName = "MoodCard";
